import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import {
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Link,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { Helmet } from "react-helmet";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";
import appLogo from "../../assets/logo1.png";

const useStyles = makeStyles((theme) => {
  const isLight = theme.mode === "light";
  const primary = theme.palette.primary.main;

  return {
    root: {
      position: "relative",
      display: "flex",
      width: "100vw",
      minHeight: "100dvh",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(3),
      overflow: "auto",
      background: isLight
        ? `radial-gradient(circle at top left, ${primary}22 0%, transparent 42%), radial-gradient(circle at bottom right, ${primary}18 0%, transparent 38%), ${theme.palette.fancyBackground}`
        : `radial-gradient(circle at top left, ${primary}33 0%, transparent 40%), radial-gradient(circle at bottom right, #1f2937 0%, transparent 45%), ${theme.palette.fancyBackground}`,
    },
    themeToggle: {
      position: "fixed",
      top: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: 2,
      backgroundColor: isLight ? "rgba(255,255,255,0.82)" : "rgba(31,41,55,0.82)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
      boxShadow: isLight
        ? "0 8px 24px rgba(15, 23, 42, 0.08)"
        : "0 8px 24px rgba(0, 0, 0, 0.28)",
      color: isLight ? "#334155" : "#e5e7eb",
      "&:hover": {
        backgroundColor: isLight ? "#ffffff" : "#374151",
      },
    },
    shell: {
      width: "100%",
      maxWidth: 980,
      display: "grid",
      gridTemplateColumns: "1fr 460px",
      gap: theme.spacing(4),
      alignItems: "stretch",
      animation: "$fadeIn 0.55s ease-out",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
        maxWidth: 460,
      },
    },
    brandPanel: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: theme.spacing(4, 2),
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    brandBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(1),
      width: "fit-content",
      padding: "6px 12px",
      borderRadius: 999,
      marginBottom: theme.spacing(2),
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.4,
      color: isLight ? primary : "#dbeafe",
      backgroundColor: isLight ? `${primary}14` : "rgba(255,255,255,0.06)",
      border: `1px solid ${isLight ? `${primary}22` : "rgba(255,255,255,0.08)"}`,
    },
    brandTitle: {
      fontSize: 42,
      lineHeight: 1.1,
      fontWeight: 800,
      color: isLight ? "#0f172a" : "#f8fafc",
      marginBottom: theme.spacing(1.5),
    },
    brandSubtitle: {
      fontSize: 16,
      lineHeight: 1.6,
      maxWidth: 420,
      color: isLight ? "#64748b" : "#94a3b8",
      marginBottom: theme.spacing(3),
    },
    featureList: {
      display: "grid",
      gap: theme.spacing(1.25),
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      color: isLight ? "#475569" : "#cbd5e1",
      fontSize: 14,
    },
    featureDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: primary,
      flexShrink: 0,
    },
    formCard: {
      borderRadius: 24,
      padding: theme.spacing(4, 3.5),
      backgroundColor: isLight ? "rgba(255,255,255,0.88)" : "rgba(17,24,39,0.88)",
      backdropFilter: "blur(18px)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
      boxShadow: isLight
        ? "0 24px 60px rgba(15, 23, 42, 0.10)"
        : "0 24px 60px rgba(0, 0, 0, 0.35)",
    },
    mobileBrand: {
      display: "none",
      textAlign: "center",
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        display: "block",
      },
    },
    logo: {
      height: 42,
      width: "auto",
      maxWidth: 180,
      objectFit: "contain",
      marginBottom: theme.spacing(1),
    },
    formTitle: {
      fontSize: 28,
      fontWeight: 700,
      color: isLight ? "#0f172a" : "#f8fafc",
      marginBottom: theme.spacing(0.5),
    },
    formSubtitle: {
      fontSize: 14,
      color: isLight ? "#64748b" : "#94a3b8",
      marginBottom: theme.spacing(3),
    },
    textField: {
      marginBottom: theme.spacing(2),
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        backgroundColor: isLight ? "#f8fafc" : "#0f172a",
        color: isLight ? "#0f172a" : "#f8fafc",
        "& fieldset": {
          borderColor: isLight ? "#e2e8f0" : "#374151",
        },
        "&:hover fieldset": {
          borderColor: isLight ? "#cbd5e1" : "#4b5563",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 2,
        },
      },
      "& .MuiInputLabel-root": {
        color: isLight ? "#64748b" : "#94a3b8",
        "&.Mui-focused": {
          color: primary,
        },
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        color: isLight ? "#64748b" : "#94a3b8",
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 4,
      },
    },
    planSelect: {
      marginBottom: theme.spacing(2),
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        backgroundColor: isLight ? "#f8fafc" : "#0f172a",
        color: isLight ? "#0f172a" : "#f8fafc",
        "& fieldset": {
          borderColor: isLight ? "#e2e8f0" : "#374151",
        },
        "&:hover fieldset": {
          borderColor: isLight ? "#cbd5e1" : "#4b5563",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 2,
        },
      },
      "& .MuiInputLabel-root": {
        color: isLight ? "#64748b" : "#94a3b8",
        "&.Mui-focused": {
          color: primary,
        },
      },
    },
    submitButton: {
      borderRadius: 14,
      padding: "13px 16px",
      fontWeight: 700,
      fontSize: 15,
      textTransform: "none",
      width: "100%",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
      color: "#fff",
      background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)`,
      boxShadow: `0 10px 24px ${primary}44`,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: `0 14px 28px ${primary}55`,
        background: `linear-gradient(135deg, ${primary} 0%, ${primary} 100%)`,
      },
    },
    loginLink: {
      display: "block",
      textAlign: "center",
      fontSize: 14,
      fontWeight: 600,
      color: primary,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
        transform: "translateY(16px)",
      },
      "100%": {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
  };
});

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  companyName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  phone: Yup.string().required("Required"),
});

const SignUp = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { toggleColorMode } = useContext(ColorModeContext);
  const history = useHistory();
  const { getPlanList } = usePlans();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
  };

  const [user] = useState(initialState);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;

  const logoSrc =
    theme.mode === "light"
      ? theme.calculatedLogoLight?.() || theme.appLogoLight || appLogo
      : theme.calculatedLogoDark?.() || theme.appLogoDark || appLogo;

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }

        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);

        if (!isEnabled) {
          toast.info("El registro de nuevos usuarios está deshabilitado.");
          history.push("/login");
        }
      } catch (err) {
        console.error("Error al verificar userCreation:", err);
        setUserCreationEnabled(false);
        toast.error("Error al verificar permiso de registro.");
        history.push("/login");
      }
    };

    fetchUserCreationStatus();
  }, [backendUrl, history]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);
      setLoading(false);
    };
    fetchData();
  }, [getPlanList]);

  const handleSignUp = async (values) => {
    try {
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  if (!userCreationEnabled) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{i18n.t("signup.title")} - NaviCRM</title>
      </Helmet>

      <div className={classes.root}>
        <IconButton
          className={classes.themeToggle}
          onClick={toggleColorMode}
          aria-label="Cambiar tema"
        >
          {theme.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>

        <div className={classes.shell}>
          <div className={classes.brandPanel}>
            <span className={classes.brandBadge}>NaviCRM · WhatsApp CRM</span>
            <Typography className={classes.brandTitle}>
              Empieza en minutos.
            </Typography>
            <Typography className={classes.brandSubtitle}>
              Crea tu organización y comienza a gestionar conversaciones,
              tickets y equipos con una plataforma moderna.
            </Typography>
            <div className={classes.featureList}>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Configuración rápida de empresa y plan
              </div>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Acceso multicanal desde el primer día
              </div>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Interfaz clara en modo claro y oscuro
              </div>
            </div>
          </div>

          <Paper elevation={0} className={classes.formCard}>
            <div className={classes.mobileBrand}>
              <img src={logoSrc} alt="NaviCRM" className={classes.logo} />
            </div>

            <Typography className={classes.formTitle}>
              {i18n.t("signup.title")}
            </Typography>
            <Typography className={classes.formSubtitle}>
              Crea tu cuenta y comienza a gestionar tus conversaciones
            </Typography>

            <Formik
              initialValues={user}
              enableReinitialize
              validationSchema={UserSchema}
              onSubmit={(values, actions) => {
                setTimeout(() => {
                  handleSignUp(values);
                  actions.setSubmitting(false);
                }, 400);
              }}
            >
              {({ touched, errors, isSubmitting }) => (
                <Form>
                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="companyName"
                    label={i18n.t("signup.form.company")}
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                    name="companyName"
                    autoComplete="organization"
                    autoFocus
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    autoComplete="name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    fullWidth
                    id="name"
                    label={i18n.t("signup.form.name")}
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="email"
                    label={i18n.t("signup.form.email")}
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    autoComplete="email"
                    inputProps={{ style: { textTransform: "lowercase" } }}
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    name="password"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    label={i18n.t("signup.form.password")}
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="phone"
                    label={i18n.t("signup.form.phone")}
                    name="phone"
                    autoComplete="tel"
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth variant="outlined" className={classes.planSelect}>
                    <InputLabel id="plan-selection-label">Plan</InputLabel>
                    <Field
                      as={Select}
                      labelId="plan-selection-label"
                      id="plan-selection"
                      name="planId"
                      label="Plan"
                      required
                      disabled={loading}
                    >
                      {plans.map((plan, key) => (
                        <MenuItem key={key} value={plan.id}>
                          {plan.name} - Agentes: {plan.users} - WhatsApp: {plan.connections} - Colas: {plan.queues} - $ {plan.amount}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    className={classes.submitButton}
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting
                      ? `${i18n.t("signup.buttons.submit")}...`
                      : i18n.t("signup.buttons.submit")}
                  </Button>

                  <Link
                    component={RouterLink}
                    to="/login"
                    className={classes.loginLink}
                  >
                    {i18n.t("signup.buttons.login")}
                  </Link>
                </Form>
              )}
            </Formik>
          </Paper>
        </div>
      </div>
    </>
  );
};

export default SignUp;
